/**
 * @file: 层板商品空格均匀排列布局
 * @author: xiaolong.huang@dmall.com
 */

import * as CubeTree from '../../../cubeTree/index';
import * as Toolkit from '../../../toolkit/index';
import FrontCubeData from '../../../frontCubeData/index';
import CodiSystem from '../../../codiSystem/index';
import Utils from '../../../utils/util';
import Move from '../../../interaction/move/index';
import { layerLink } from '../link';
import { containSubGoodsWithPosWidthSizeByCubeId, hasFoldGoods, hasMoreSurface, diffWidthMainAndSub } from '../../../UIToolkit/goods/index';
import { cubeCountInLayer, layerIsGrouped, layersTotalWidth, layerCubesTotalWidth } from '../../../UIToolkit/layer/layerInfo';
import { textareaSize } from '../../../UIToolkit/textarea/index';
import { getCubeNumberValue } from '../../../UIToolkit/func/index';
import { cubeModify } from '../../../update/cubeModify';
import { layerCubeSizeContainSpace } from '../../../UIToolkit/size/index';
import { getCubeHalfInTargetWidthContainSpace } from '../../../UIToolkit/query/index';

export default function spaceAround(layerId: CubeId): void {
    let spaceTotalWidth: PhysicsMeasurement = layerSpaceWidth(layerId);

    moveBehindCubes(layerId);
    layout(layerId, spaceTotalWidth);
    moveOverflowCubes(layerId);
}

function layout(layer: CubeId, spaceTotalWidth: PhysicsMeasurement): void {
    let layoutCubeIds: CubeId[] = findLayoutCubeIds(layer);
    let count: number = cubeCountInLayer(layoutCubeIds);
    let spaceAroundWidth: PhysicsMeasurement = Toolkit.spaceAroundWidth({
        spaceTotal: spaceTotalWidth,
        moduleTotalWidth: layerCubesTotalWidth(layoutCubeIds),
        moduleCount: count
    });
    let extendLeft: PhysicsMeasurement = FrontCubeData.getNumberValue(layer, 'extendLeft');
    let startPos: PhysicsMeasurement = startLeftCompute(layer, spaceAroundWidth);
    let layerChildren: CubeId[] = CubeTree.findChildCubeIds(layer);

    layerChildren.forEach((cubeId: CubeId): void => {
        let layoutCubeSize: Size;

        // 商品中存在叠放商品，此种情况干掉商品中的间隔
        if (hasFoldGoods(cubeId)) {
            cubeModify(cubeId, {
                posWidth: 0
            });
        }

        // 商品中不存在叠放商品，且是多面宽，则给商品加间距
        else if (hasMoreSurface(cubeId)) {
            // 最宽字码和主码的宽度差，用来补齐posWidth
            let diffWidth: PhysicsMeasurement = diffWidthMainAndSub(cubeId);

            cubeModify(cubeId, {
                posWidth: Utils.toFixedNumber(spaceAroundWidth + diffWidth, 2)
            });
        }

        if (CubeTree.isGoods(cubeId)) {
            // 计算商品宽度，包含字码，包含posWidth
            layoutCubeSize = containSubGoodsWithPosWidthSizeByCubeId(cubeId);
        }
        else if (CubeTree.isTextarea(cubeId)) {
            layoutCubeSize = textareaSize(cubeId);
        }

        /**
         * todo:
         * 1. specialAttr设置
         * 2. 隔板
         */
        startPos = Utils.toFixedNumber(startPos, 2);
        cubeModify(cubeId, {
            posX: startPos,
            posY: 0
        });

        startPos += layoutCubeSize.width + spaceAroundWidth;
    });
}

// 起始坐标计算
function startLeftCompute(layerId: CubeId, spaceAroundWidth: PhysicsMeasurement): PhysicsMeasurement {
    let extendLeft: PhysicsMeasurement = FrontCubeData.getNumberValue(layerId, 'extendLeft');
    let link: LinkedListInstance|null = layerLink(layerId);

    if ((link && link.isHead(layerId)) || !link) {
        return -extendLeft + spaceAroundWidth;
    }

    let theFirstLayer: CubeId = link.findFirstNode().val;
    let theFirstLayerExtendLeft: PhysicsMeasurement = FrontCubeData.getNumberValue(layerId, 'extendLeft');
    let prevLayers: CubeId[] = link.beforeOf(layerId).reverse();
    let prevCubes: CubeId[] = CubeTree.findCubesChildren(prevLayers);
    let currentLayerLeft: PhysicsMeasurement = CodiSystem.getPhysicsCoordinate(layerId).left;

    if (!prevCubes.length) {
        let leftSum: PhysicsMeasurement = Utils.math(currentLayerLeft, theFirstLayerExtendLeft, '+');

        return Utils.math(spaceAroundWidth, leftSum, '-');
    }

    let theLastOne: CubeId = prevCubes[prevCubes.length - 1];
    let lastLayer: CubeId = CubeTree.findParentIdByCubeId(theLastOne);
    let lastLayerLeft: PhysicsMeasurement = CodiSystem.getPhysicsCoordinate(lastLayer).left;
    let left: PhysicsMeasurement = FrontCubeData.getNumberValue(theLastOne, 'posX');
    let width: PhysicsMeasurement = layerCubeSizeContainSpace(theLastOne).width;
    let theLastOneRightLine: PhysicsMeasurement = Utils.math(left, width, '+');
    let rightLine: PhysicsMeasurement = Utils.math(lastLayerLeft, theLastOneRightLine, '+');
    let prevOverFlowWidth: PhysicsMeasurement = Utils.math(rightLine, currentLayerLeft, '-');

    return Utils.math(prevOverFlowWidth, spaceAroundWidth, '+');
}

// 溢出后移
function moveOverflowCubes(layerId: CubeId): void {
    let link: LinkedListInstance|null = layerLink(layerId);
    let overflowCubes: CubeId[] = findOverFlowCube(layerId, link);
    let next: LinkedNodeInstance|null = link.findNext(layerId);
    let nextLayer: CubeId|null = next ? next.val : null;

    if (!nextLayer) {
        return;
    }

    let logicMoveTaskQueue: LogicMoveTask[] = makeOverFlowTaskQueue(overflowCubes, nextLayer);

    overflowCubes.forEach((cubeId: CubeId): void => {
        let cube: CubeNode = CubeTree.findByCubeId(cubeId);

        cube.jView.addClass('is-hide');
    });

    Move.logic(logicMoveTaskQueue);
}

function makeOverFlowTaskQueue(cubeIds: CubeId[], layerId: CubeId): LogicMoveTask[] {
    let result: LogicMoveTask[] = [];
    let insertTarget: CubeId|null = CubeTree.findTheFirstChildCubeId(layerId);

    cubeIds.forEach((cubeId: CubeId): void => {
        let oldParent: CubeId|null = CubeTree.findParentIdByCubeId(cubeId);

        result.push({
            from: {
                parent: oldParent,
                moveTarget: cubeId
            },
            to: {
                parent: layerId,
                insertBefore: insertTarget
            }
        });
        insertTarget = cubeId;
    });

    return result;
}

function findOverFlowCube(layerId: CubeId, link: LinkedListInstance): CubeId[] {
    let result: CubeId[] = [];
    let childIds: CubeId[] = CubeTree.getChildCubeIds(layerId);
    let moduleWidth: number = FrontCubeData.getNumberValue(layerId, 'moduleWidth');
    let extendLeft: number = FrontCubeData.getNumberValue(layerId, 'extendLeft');
    let extendRight: number = FrontCubeData.getNumberValue(layerId, 'extendRight');
    let totalWidth: PhysicsMeasurement = moduleWidth;

    if (link.isHead(layerId)) {
        totalWidth = Utils.math(extendLeft, totalWidth, '+');
    }

    if (link.isLast(layerId)) {
        totalWidth = Utils.math(extendRight, totalWidth, '+');
    }

    for (let i = childIds.length - 1; i >= 0; i--) {
        let left: PhysicsMeasurement = FrontCubeData.getNumberValue(childIds[i], 'posX');

        left >= totalWidth && result.push(childIds[i]);
    }

    return result;
}

function makeFillTaskQueue(cubeIds: CubeId[], layerId: CubeId): LogicMoveTask[] {
    let result: LogicMoveTask[] = [];

    cubeIds.forEach((cubeId: CubeId): void => {
        let oldParent: CubeId|null = CubeTree.findParentIdByCubeId(cubeId);

        result.push({
            from: {
                parent: oldParent,
                moveTarget: cubeId
            },
            to: {
                parent: layerId,
                insertBefore: null
            }
        });
    });

    return result;
}

// 计算层板空置区域宽度
function layerEmptyWidth(layerId: CubeId, end: PhysicsMeasurement): PhysicsMeasurement {
    let result: PhysicsMeasurement = 0;
    let grouped: boolean = layerIsGrouped(layerId);
    let moduleWidth: PhysicsMeasurement = FrontCubeData.getNumberValue(layerId, 'moduleWidth');
    let extendLeft: PhysicsMeasurement = FrontCubeData.getNumberValue(layerId, 'extendLeft');
    let extendRight: PhysicsMeasurement = FrontCubeData.getNumberValue(layerId, 'extendRight');
    let totalWidth: PhysicsMeasurement = moduleWidth;

    if (grouped) {
        let link: LinkedListInstance|null = layerLink(layerId);

        if (link.isHead(layerId)) {
            totalWidth = Utils.math(extendLeft, totalWidth, '+');
        }

        if (link.isLast(layerId)) {
            totalWidth = Utils.math(extendRight, totalWidth, '+');
        }
    }
    else {
        totalWidth = Utils.math(extendLeft, totalWidth, '+');
        totalWidth = Utils.math(extendRight, totalWidth, '+');
    }

    result = Utils.math(totalWidth, end, '-');

    return result;
}

function moveBehindCubes(layer: CubeId): void {
    let behindLayers: CubeId[] = [];
    let behindCubes: CubeId[] = [];
    let link: LinkedListInstance|null = layerLink(layer);

    behindLayers = link.behindOf(layer);
    behindLayers.forEach((layer: CubeId): void => {
        behindCubes = behindCubes.concat(CubeTree.findChildCubeIds(layer));
    });

    let moveTask: LogicMoveTask[] = makeFillTaskQueue(behindCubes, layer);

    Move.logic(moveTask);
}

function findLayoutCubeIds(layerId: CubeId): CubeId[] {
    let result: CubeId[] = [];
    let link: LinkedListInstance|null = layerLink(layerId);
    let linkLayers: CubeId[] = link.valueList();

    result = CubeTree.findCubesChildren(linkLayers);

    return result;
}

function layerSpaceWidth(layerId: CubeId): PhysicsMeasurement {
    let grouped: boolean = layerIsGrouped(layerId);
    let result: PhysicsMeasurement = 0;
    let link: LinkedListInstance|null = layerLink(layerId);
    let linkLayers: CubeId[] = link.valueList();
    let layersWidth: PhysicsMeasurement = layersTotalWidth(linkLayers);
    let linkHead: LinkedNodeInstance|null = link.findFirstNode();
    let linkLast: LinkedNodeInstance|null = link.findLastNode();
    let headLayer: CubeId = linkHead.val;
    let lastLayer: CubeId = linkLast.val;
    let extendLeft: PhysicsMeasurement = FrontCubeData.getNumberValue(headLayer, 'extendLeft');
    let extendRight: PhysicsMeasurement = FrontCubeData.getNumberValue(lastLayer, 'extendRight');

    result = Utils.math(extendLeft, layersWidth, '+');
    result = Utils.math(extendRight, result, '+');

    return result;
}
